// NOTE variants are now called alternates
import aampeTheme from '../constants/aampeTheme';
import ICONS from '../constants/icons';

import { formatQueryParam } from '../helpers/stringHelpers';

import { UnlabelledFormula } from './formula.model';
import { LabelModel, VarintLabel } from './label.model';

export enum VariantType {
    Greeting = 'Greeting',
    Offering = 'Offering',
    ValueProposition = 'ValueProposition',
    CallToAction = 'CallToAction',
    Incentive = 'Incentive',
    Other = 'Other',
    Tone = 'Tone',
    Image = 'Image',
}

export enum AllVariantType {
    All = 'All',
}

export type AllVariantTypes = string | AllVariantType;

export const VariantTypeData: {
    [key in AllVariantTypes]: VariantTypeDataModel;
} = {
    [VariantType.ValueProposition]: {
        key: formatQueryParam('Value Proposition'),
        color: aampeTheme.colors.blueneutral09,
        bgColor: aampeTheme.variantTypeColors.ValueProposition,
        hoverBgColor: aampeTheme.variantTypeHoverBgColors.ValueProposition,
        text: 'Value proposition',
        title: 'Speak to different user motivations with Value Proposition labels:',
        description: [
            'When sending messages, we want to speak to our users in ways that they appreciate.',
            'For example, just because someone likes “coffee,” doesn’t mean they like “inexpensive coffee”.',
            'Aampe uses Value Proposition labels to send messages based on the values each user cares about, for each of your Offerings.',
        ],
        example:
            '“affordability”, “quality craftsmanship”, “sustainability”, "comfort"',
        tip: 'What are your users’ “whys?” Your Value Propositions define what your users appreciate about each of your offerings',
        icon: ICONS.valueProposition,
        iconNew: ICONS.valuePropositionNew,
    },
    [VariantType.Offering]: {
        key: formatQueryParam('Offering'),
        color: aampeTheme.colors.fuchsia600,
        bgColor: aampeTheme.variantTypeColors.Offering,
        hoverBgColor: aampeTheme.variantTypeHoverBgColors.Offering,
        text: 'Offering',
        title: 'Cross-sell your products using the Offering label:',
        description: [
            'Aampe can turn your messages into effective cross-selling tools, but we need your help!',
            'Why would a user who is interested in one item be attracted to another item? What’s the common thread that ties these items together?',
            'Use Offering labels to associate related messages in your catalog and enable cross selling.',
        ],
        example:
            '“Italian Food”, “Vegan recipes”, “High top Shoes”, “Basketball Equipment”',
        tip: 'Offering labels outline how your different offerings relate to one another. Use this tab to see which of your Offering labels are currently resonating with your users.',
        icon: ICONS.offering,
        iconNew: ICONS.offeringNew,
    },
    [VariantType.Greeting]: {
        key: formatQueryParam('Greeting'),
        color: aampeTheme.colors.blueLight500,
        bgColor: aampeTheme.variantTypeColors.Greeting,
        hoverBgColor: aampeTheme.variantTypeHoverBgColors.Greeting,
        text: 'Greeting',
        title: 'Define your Greeting labels',
        description: ['How are you getting your user’s attention?'],
        example: '“Hello”, “Welcome to our app”, “Check this out!”',
        tip: 'Which Greetings are resonating best with your users?',
        icon: ICONS.greeting,
        iconNew: ICONS.greetingNew,
    },
    [VariantType.CallToAction]: {
        key: formatQueryParam('Call To Action'),
        color: aampeTheme.colors.lime600,
        bgColor: aampeTheme.variantTypeColors.CallToAction,
        hoverBgColor: aampeTheme.variantTypeHoverBgColors.CallToAction,
        text: 'Call to action',
        title: 'Define your Call To Action labels',
        description: ['How are you encouraging users to to click right now?'],
        example: '“shop now”, “today only”, “while supplies last”',
        tip: 'Which Call-to-Actions are working most effectively right now?',
        icon: ICONS.callToAction,
        iconNew: ICONS.callToActionNew,
    },
    [VariantType.Incentive]: {
        key: formatQueryParam('Incentive'),
        color: aampeTheme.colors.secondary500,
        bgColor: aampeTheme.variantTypeColors.Incentive,
        hoverBgColor: aampeTheme.variantTypeHoverBgColors.Incentive,
        text: 'Incentive',
        title: 'Define your Incentive labels',
        description: [
            'Different users need different levels of encouragement to get them over the conversion hump.',
            'What kinds of discounts, promotions or other incentives would sweeten the deal for your users?',
        ],
        example: '“free delivery”, “30% off”, “buy 1, get 1 free”',
        tip: 'Which incentives (e.g. discounts and offers) are working best right now?',
        icon: ICONS.incentive,
        iconNew: ICONS.incentiveNew,
    },
    [VariantType.Other]: {
        key: formatQueryParam('Other'),
        color: aampeTheme.colors.turquoise500,
        bgColor: aampeTheme.variantTypeColors.Other,
        hoverBgColor: aampeTheme.variantTypeHoverBgColors.Other,
        text: 'Other',
        title: 'Define your Other labels',
        description: ['Use this as a placeholder for additional items.'],
        example: 'Emojis, bits of text you’d like to test, etc.',
        tip: 'See the performance of all of your variants labeled with “Other”',
        icon: ICONS.other,
        iconNew: ICONS.otherNew,
    },
    [VariantType.Tone]: {
        key: formatQueryParam('Tone'),
        color: aampeTheme.colors.error500,
        bgColor: aampeTheme.variantTypeColors.Tone,
        hoverBgColor: aampeTheme.variantTypeHoverBgColors.Tone,
        text: 'Tone',
        title: 'Define your Tone labels',
        description: ['In what tone of voice are you speaking to your users?'],
        example: `Buy now! is "Urgent,"Try it yourself! is "Curiosity,"You'll love it! is "Joyful"`,
        tip: 'Which messaging Tones are currently resonating with your users?',
        icon: ICONS.tone,
        iconNew: ICONS.toneNew,
    },
    [VariantType.Image]: {
        key: formatQueryParam('Image'),
        color: aampeTheme.variantTypeColors.Image,
        bgColor: `${aampeTheme.variantTypeColors.Image}80`,
        text: 'Image',
        title: 'Define your Image labels',
        description: [],
        example: '',
        icon: ICONS.imageVariant,
        iconNew: ICONS.imageVariant,
    },
    [AllVariantType.All]: {
        key: formatQueryParam('All'),
        color: aampeTheme.colors.textAccessibleWG01,
        bgColor: aampeTheme.colors.white,
        text: 'All',
        description: ['All variants'],
        icon: null,
        iconNew: null,
        title: 'All variants',
        example: '',
        tip: '',
    },
};

export interface VariantTypeDataModel {
    key: string;
    title: string;
    color: string;
    text: string;
    description: string[];
    example: string;
    tip?: string;
    icon: any;
    iconNew: any;
    bgColor: string;
    hoverBgColor?: string;
}

export interface VariantModel {
    variant_id: string;
    labels: LabelModel[];
    text: string;
}

export interface VariantModelV1 {
    id: string;
    label: VarintLabel;
    content: string;
}
export interface VariantCreateModel {
    id: string;
    formula_id: number;
    text: string;
    variant_type: string;
    label_id?: number;
}

export interface VariantBase {
    id: string;
    component_id: string;
    content: string;
    variant_type: string;
    component_type_id: string;
}

export interface UnlabelledVariant extends VariantBase {
    formula: UnlabelledFormula;
    message_content: {
        id: string;
    };
}

export interface VariantWithLabel extends VariantBase {
    label?: VarintLabel;
}

export interface TestFormulaVariantData extends VariantWithLabel {
    isDisabled: boolean;
}

export interface VariantCreate {
    component_id: string;
    content: string;
    label_id?: number;
}

export interface VariantUpdatePayload {
    content: string;
    label_id?: number;
}
export interface VarintUpdateRequest {
    id: string;
    data: VariantUpdatePayload;
}

export interface VariantBulkDelete {
    variant_ids: string[];
}

export interface VariantUpdateBulk {
    content: string;
    id: string;
    label_id?: number | null;
}

export interface VariantCreateBulk {
    component_id: string;
    label_id?: number;
    content: string;
}
